import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import { createRemindersForRelease, REMINDER_TEMPLATES } from '../../utils/remindersData';
import { remindersApi } from '../../utils/remindersApi';

const ManualAddReminder = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [reminderData, setReminderData] = useState({
        reminder_type: 'release', // release, booking, catalog, merch, custom
        title: '',
        description: '',
        due_date: '',
        priority: 'medium', // low, medium, high, critical
        category: '',
        // Release-specific fields
        release_name: '',
        artist_name: '',
        label: '',
        distributor: '',
        // Booking-specific fields
        venue_name: '',
        booking_date: '',
        contact_person: '',
        // Team assignee fields
        assignee_name: '',
        assignee_role: '',
        assignee_email: '',
        assignee_company: '',
        // Generic fields
        notes: '',
        tags: '',
        reminder_frequency: 'once', // once, daily, weekly, monthly
        snooze_options: '24', // hours
        completion_action: 'mark_done' // mark_done, delete, archive
    });

    const reminderTypes = [
        { value: 'release', label: 'Music Release', description: 'Release workflow and deadlines' },
        { value: 'booking', label: 'Live Shows & Bookings', description: 'Show preparations and follow-ups' },
        { value: 'catalog', label: 'Catalog Management', description: 'Track submissions and updates' },
        { value: 'merch', label: 'Merchandise', description: 'Merch production and sales' },
        { value: 'promotion', label: 'Promotion & Marketing', description: 'Campaign deadlines and tasks' },
        { value: 'business', label: 'Business & Admin', description: 'Contracts, payments, meetings' },
        { value: 'custom', label: 'Custom Reminder', description: 'Anything else you need to track' }
    ];

    const priorityOptions = [
        { value: 'low', label: 'Low', color: '#34C759' },
        { value: 'medium', label: 'Medium', color: '#007AFF' },
        { value: 'high', label: 'High', color: '#FF3B30' },
        { value: 'critical', label: 'Critical', color: '#FF3B30' }
    ];

    const handleSave = async () => {
        setIsLoading(true);
        try {
            if (!reminderData.title || !reminderData.due_date) {
                dispatch(setErrorAlert('Please enter a title and due date'));
                setIsLoading(false);
                return;
            }

            if (!reminderData.assignee_name || !reminderData.assignee_role) {
                dispatch(setErrorAlert('Please enter assignee name and role'));
                setIsLoading(false);
                return;
            }

            if (reminderData.reminder_type === 'release' && reminderData.release_name) {
                // Create full release workflow
                const releaseId = `custom_release_${Date.now()}`;
                const metadata = {
                    artist: reminderData.artist_name,
                    label: reminderData.label,
                    distributor: reminderData.distributor,
                    assignee_name: reminderData.assignee_name,
                    assignee_role: reminderData.assignee_role,
                    assignee_email: reminderData.assignee_email,
                    assignee_company: reminderData.assignee_company,
                    custom: true
                };

                const newReminders = createRemindersForRelease(
                    releaseId,
                    reminderData.release_name,
                    reminderData.due_date,
                    metadata
                );

                // Show the reminder flow modal instead of just an alert
                if (window.showReminderFlowModal) {
                    window.showReminderFlowModal(reminderData.release_name, newReminders.length);
                } else {
                    dispatch(setSuccessAlert(`Created ${newReminders.length} release workflow reminders`));
                }
            } else {
                // Create single custom reminder data
                const customReminderData = {
                    title: reminderData.title,
                    description: reminderData.description,
                    due_date: reminderData.due_date,
                    priority: reminderData.priority,
                    category: reminderData.reminder_type,
                    type: 'custom',
                    assignee_name: reminderData.assignee_name,
                    assignee_role: reminderData.assignee_role,
                    assignee_email: reminderData.assignee_email,
                    assignee_company: reminderData.assignee_company,
                    ...reminderData
                };

                // Try to create via API first, fallback to local storage
                const apiResult = await remindersApi.createCustomReminder(customReminderData);
                
                if (apiResult.success) {
                    dispatch(setSuccessAlert('Custom reminder created successfully'));
                } else {
                    console.log('API creation failed, using local storage fallback:', apiResult.error);
                    
                    // Fallback to local storage method
                    const localCustomReminder = {
                        id: `custom_${Date.now()}`,
                        ...customReminderData,
                        metadata: {
                            ...reminderData,
                            assignee_name: reminderData.assignee_name,
                            assignee_role: reminderData.assignee_role,
                            assignee_email: reminderData.assignee_email,
                            assignee_company: reminderData.assignee_company,
                            custom: true,
                            created_at: new Date().toISOString()
                        },
                        status: 'pending',
                        viewed: false
                    };

                    // Add custom reminder using the local reminders manager
                    const { addCustomReminder } = await import('../../utils/remindersData');
                    const localResult = addCustomReminder(localCustomReminder);
                    
                    if (localResult) {
                        dispatch(setSuccessAlert('Custom reminder created successfully (offline)'));
                    } else {
                        throw new Error('Failed to create reminder locally');
                    }
                }
            }

            navigate('/reminders');
        } catch (error) {
            console.error('Error creating reminder:', error);
            dispatch(setErrorAlert('Failed to create reminder'));
        } finally {
            setIsLoading(false);
        }
    };

    const getTypeSpecificFields = () => {
        switch (reminderData.reminder_type) {
            case 'release':
                return [
                    { key: 'release_name', label: 'Release Name', placeholder: 'Enter song/album title', required: true },
                    { key: 'artist_name', label: 'Artist Name', placeholder: 'Enter artist name' },
                    { key: 'label', label: 'Record Label', placeholder: 'Enter record label' },
                    { key: 'distributor', label: 'Distributor', placeholder: 'Enter distributor (e.g., DistroKid, CD Baby)' }
                ];
            case 'booking':
                return [
                    { key: 'venue_name', label: 'Venue Name', placeholder: 'Enter venue name' },
                    { key: 'booking_date', label: 'Show Date', placeholder: 'Select show date', type: 'date' },
                    { key: 'contact_person', label: 'Contact Person', placeholder: 'Enter venue contact' }
                ];
            case 'catalog':
                return [
                    { key: 'track_title', label: 'Track Title', placeholder: 'Enter track name' },
                    { key: 'submission_platform', label: 'Platform', placeholder: 'e.g., Spotify, Apple Music' }
                ];
            case 'merch':
                return [
                    { key: 'product_name', label: 'Product Name', placeholder: 'Enter merchandise item' },
                    { key: 'supplier', label: 'Supplier', placeholder: 'Enter supplier name' },
                    { key: 'order_quantity', label: 'Quantity', placeholder: 'Enter order quantity' }
                ];
            case 'promotion':
                return [
                    { key: 'campaign_name', label: 'Campaign Name', placeholder: 'Enter campaign title' },
                    { key: 'platform', label: 'Platform', placeholder: 'e.g., Instagram, TikTok, Radio' },
                    { key: 'budget', label: 'Budget', placeholder: 'Enter budget amount' }
                ];
            case 'business':
                return [
                    { key: 'contract_type', label: 'Type', placeholder: 'e.g., Recording Contract, Publishing Deal' },
                    { key: 'party_involved', label: 'Other Party', placeholder: 'Enter company/person name' },
                    { key: 'value', label: 'Value', placeholder: 'Enter monetary value if applicable' }
                ];
            default:
                return [];
        }
    };

    const baseFields = [
        { key: 'title', label: 'Reminder Title', placeholder: 'Enter a clear title for this reminder', required: true },
        { key: 'description', label: 'Description', placeholder: 'Describe what needs to be done', type: 'textarea' },
        { key: 'due_date', label: 'Due Date', placeholder: 'Select due date', type: 'date', required: true },
        { key: 'priority', label: 'Priority Level', type: 'select', options: priorityOptions }
    ];

    const teamAssigneeFields = [
        { key: 'assignee_name', label: 'Assignee Name', placeholder: 'Enter team member name', required: true },
        { key: 'assignee_role', label: 'Role', placeholder: 'Enter their role/position', required: true },
        { key: 'assignee_email', label: 'Email (Optional)', placeholder: 'Enter email address', type: 'email' },
        { key: 'assignee_company', label: 'Company (Optional)', placeholder: 'Enter company name' }
    ];

    const additionalFields = [
        { key: 'tags', label: 'Tags', placeholder: 'Enter tags separated by commas (optional)' },
        { key: 'notes', label: 'Additional Notes', placeholder: 'Any additional information', type: 'textarea' }
    ];

    const allFields = [
        { key: 'reminder_type', label: 'Reminder Type', type: 'select', options: reminderTypes, required: true },
        ...baseFields.slice(0, 1), // title
        ...getTypeSpecificFields(),
        ...baseFields.slice(1, 3), // description, due_date
        ...teamAssigneeFields,
        ...baseFields.slice(3), // priority
        ...additionalFields
    ];

    return (
        <LayoutHeader>
            <div className="flex justify-center w-full">
                <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
                    {/* Header */}
                    <div className="px-4 my-[50px] flex w-full justify-between items-center">
                        <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Add Custom Reminder</span>
                        <div className="flex items-center gap-4">
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate('/reminders')}
                            >
                                View All
                            </span>
                            <span 
                                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                                onClick={() => navigate(-1)}
                            >
                                Go back
                            </span>
                        </div>
                    </div>

                    <div className="w-full px-4 flex-1 overflow-y-auto">
                        <div className="flex flex-col">
                            <div className="mb-6">
                                <span className="text-[14px] text-white" style={{ fontWeight: 300 }}>Create a custom reminder</span>
                                <p className="text-[10px] text-[rgba(255,255,255,0.7)] mt-1">
                                    Set up reminders for releases, bookings, or any other tasks you need to track. 
                                    Assign team members and track responsibilities.
                                </p>
                            </div>

                            {/* Type Selection Notice */}
                            {reminderData.reminder_type === 'release' && (
                                <div className="mb-6 p-3 bg-[rgba(255,153,0,0.1)] rounded border border-[rgba(255,153,0,0.2)]">
                                    <p className="text-[rgba(255,255,255,0.8)] text-[11px] leading-relaxed">
                                        <strong>Release Workflow:</strong> This will create a complete 9-step release workflow 
                                        with reminders starting 5 weeks before release and ending 2 weeks after.
                                    </p>
                                </div>
                            )}

                            {/* Form Fields */}
                            <div className="space-y-4 mb-6">
                                {allFields.map((field) => (
                                    <div 
                                        key={field.key}
                                        className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all"
                                    >
                                        <label className="block text-white text-[12px] font-semibold mb-2">
                                            {field.label}
                                            {field.required && <span className="text-red-400 ml-1">*</span>}
                                        </label>
                                        
                                        {field.type === 'textarea' ? (
                                            <textarea
                                                value={reminderData[field.key] || ''}
                                                onChange={(e) => setReminderData({
                                                    ...reminderData,
                                                    [field.key]: e.target.value
                                                })}
                                                placeholder={field.placeholder}
                                                className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] resize-none h-20"
                                                required={field.required}
                                            />
                                        ) : field.type === 'select' ? (
                                            <select
                                                value={reminderData[field.key] || ''}
                                                onChange={(e) => setReminderData({
                                                    ...reminderData,
                                                    [field.key]: e.target.value
                                                })}
                                                className="w-full bg-transparent border-none outline-none text-white text-[12px] cursor-pointer"
                                                required={field.required}
                                            >
                                                {field.key === 'reminder_type' && (
                                                    <option value="" disabled>
                                                        Select reminder type
                                                    </option>
                                                )}
                                                {field.options?.map((option) => (
                                                    <option 
                                                        key={option.value} 
                                                        value={option.value} 
                                                    >
                                                        {option.label}
                                                        {option.description && ` - ${option.description}`}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type={field.type || 'text'}
                                                value={reminderData[field.key] || ''}
                                                onChange={(e) => setReminderData({
                                                    ...reminderData,
                                                    [field.key]: e.target.value
                                                })}
                                                placeholder={field.placeholder}
                                                className="w-full bg-transparent border-none outline-none text-white text-[12px] placeholder-[rgba(255,255,255,0.5)]"
                                                required={field.required}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Save Button */}
                            <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all">
                                <button
                                    onClick={handleSave}
                                    disabled={isLoading}
                                    className="w-full bg-[#FF3B30] text-white text-[12px] font-semibold py-3 rounded-lg hover:bg-[rgba(255,59,48,0.9)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                            Creating Reminder...
                                        </>
                                    ) : (
                                        'Create Reminder'
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Help Text */}
                        <div className="mt-6 mb-4">
                            <div className="text-[12px] text-[rgba(255,255,255,0.5)] leading-[1.5] max-w-full">
                                <p>Choose the type of reminder that matches your needs.</p>
                                <p>Release reminders create a full workflow, while other types create single reminders.</p>
                                <p>Assign team members to track responsibilities and contact information.</p>
                                <p>You can always edit or delete reminders later from the main reminders page.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LayoutHeader>
    );
};

export default ManualAddReminder; 